/**
 * TOTP (RFC 6238) over HMAC (RFC 4226). Generates one-time codes from
 * from base32 seed (`otpauth://` format of authenticators).
 *
 * Compatible with Google Authenticator / Authy (SHA-1, 6 digits, 30s period
 * by default). Local calculation: seed lives encrypted inside the item.
 */

import { base32Decode } from "./encoding";
import { hmac } from "./primitives";

export interface TotpConfig {
  readonly digits?: number;
  readonly period?: number;
  readonly algorithm?: "SHA-1" | "SHA-256" | "SHA-512";
  /** Milliseconds; defaults to Date.now(). */
  readonly timestamp?: number;
}

function counterBytes(counter: number): Uint8Array {
  const buf = new Uint8Array(8);
  // 64-bit big-endian counter (upper bits ~0 in practice).
  let value = counter;
  for (let i = 7; i >= 0; i--) {
    buf[i] = value & 0xff;
    value = Math.floor(value / 256);
  }
  return buf;
}

/** Current TOTP code for given base32 seed. */
export async function generateTotp(secretBase32: string, config: TotpConfig = {}): Promise<string> {
  const { digits = 6, period = 30, algorithm = "SHA-1", timestamp = Date.now() } = config;
  const key = base32Decode(secretBase32);
  const counter = Math.floor(timestamp / 1000 / period);
  const mac = await hmac(key, counterBytes(counter), algorithm);
  // Dynamic truncation (RFC 4226 §5.3).
  const offset = (mac.at(-1) ?? 0) & 0x0f;
  const binary =
    (((mac[offset] ?? 0) & 0x7f) << 24) |
    (((mac[offset + 1] ?? 0) & 0xff) << 16) |
    (((mac[offset + 2] ?? 0) & 0xff) << 8) |
    ((mac[offset + 3] ?? 0) & 0xff);
  return (binary % 10 ** digits).toString().padStart(digits, "0");
}

/** Seconds remaining for current code validity (for countdown UI). */
export function totpTimeRemaining(config: Pick<TotpConfig, "period" | "timestamp"> = {}): number {
  const { period = 30, timestamp = Date.now() } = config;
  return period - (Math.floor(timestamp / 1000) % period);
}

/** Extracts base32 seed from an `otpauth://totp/...?secret=XXXX` URI. */
export function parseOtpauthSecret(uri: string): string | undefined {
  const match = uri.match(/[?&]secret=([^&]+)/i);
  return match ? match[1] : undefined;
}
